import { Injectable, OnDestroy, inject } from '@angular/core';
import {
  BehaviorSubject,
  from,
  interval,
  Observable,
  of,
  Subject,
  Subscription,
} from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { CamarasService } from '../../../auxiliares/servicios/http/camara.service';

export interface Descarga {
  url?: string;
  token?: string;
  ready?: boolean;
  idCamara?: string;
  fechaCreacion?: Date;
  nombre?: string;
  tipo?: 'video' | 'excel';
  duracionMs?: number;
  jobId?: string;
  progreso?: number;
  /** Mensaje de error: la fila queda visible para que el usuario sepa qué pasó. */
  error?: string;
}

/** Estado mínimo que devuelve el polling de un job de export asíncrono. */
export interface DescargaJobEstado {
  estado: 'pendiente' | 'procesando' | 'completado' | 'error';
  urlDescarga?: string;
  error?: string;
  progreso?: number;
}

@Injectable({ providedIn: 'root' })
export class DescargasService implements OnDestroy {
  private camarasService = inject(CamarasService);

  private listadoDescargas: Descarga[] = [];
  private subscriptions = new Map<string, Subscription>();

  // BehaviorSubject para que los componentes se suscriban a cambios en el listado
  private descargasSubject = new BehaviorSubject<Descarga[]>([]);
  public descargas$: Observable<Descarga[]> =
    this.descargasSubject.asObservable();

  // Subject para notificar cuando una descarga pasa de false a true
  private nuevaDescargaListaSubject = new Subject<Descarga>();
  public nuevaDescargaLista$: Observable<Descarga> =
    this.nuevaDescargaListaSubject.asObservable();

  constructor() {
    this.cargarDesdeStorage();
  }

  public agregarDescargaVideo(
    idCamara: string,
    taskId: string,
    nombre?: string,
  ) {
    this.listadoDescargas.push({
      token: taskId,
      ready: false,
      idCamara,
      fechaCreacion: new Date(),
      nombre: nombre || `Descarga ${taskId.substring(0, 8)}`,
    });
    this.guardarEnStorage();
    this.notificarCambios();
    this.iniciarPollingDescarga(idCamara, taskId);
  }

  public getDescargas(): Descarga[] {
    return [...this.listadoDescargas];
  }

  public getCantidadPendientes(): number {
    return this.listadoDescargas.filter((d) => !d.ready).length;
  }

  public eliminarDescarga(token: string): void {
    this.listadoDescargas = this.listadoDescargas.filter(
      (d) => d.token !== token,
    );
    this.guardarEnStorage();
    this.notificarCambios();
    this.detenerPollingDescarga(token);
  }

  private iniciarPollingDescarga(idCamara: string, taskId: string): void {
    // Si ya existe una subscripción para este taskId, no crear otra
    if (this.subscriptions.has(taskId)) {
      return;
    }

    // Crear subscripción que consulta cada 10 segundos
    const subscription = interval(10000)
      .pipe(
        switchMap(() =>
          from(this.camarasService.downloadVideo(idCamara, taskId)).pipe(
            catchError((error) => {
              // En caso de error, devolver string vacío para continuar intentando
              console.warn(
                `Error temporal al verificar descarga ${taskId}:`,
                error,
              );
              return of('');
            }),
          ),
        ),
      )
      .subscribe((url: string) => {
        // Solo procesar si tenemos una URL válida
        if (url && url.trim() !== '') {
          const descarga = this.listadoDescargas.find(
            (d) => d.token === taskId,
          );
          if (descarga && !descarga.ready) {
            descarga.url = url;
            descarga.ready = true;
            this.guardarEnStorage();
            this.notificarCambios();

            // Notificar que hay una nueva descarga lista
            this.nuevaDescargaListaSubject.next(descarga);
          }

          // Cancelar la subscripción una vez que está lista
          this.detenerPollingDescarga(taskId);
        }
      });

    this.subscriptions.set(taskId, subscription);
  }

  private detenerPollingDescarga(taskId: string): void {
    const subscription = this.subscriptions.get(taskId);
    if (subscription) {
      subscription.unsubscribe();
      this.subscriptions.delete(taskId);
    }
  }

  private guardarEnStorage(): void {
    const toSave = this.listadoDescargas.filter((d) => d.tipo !== 'excel');
    sessionStorage.setItem('listadoDescargas', JSON.stringify(toSave));
  }

  private cargarDesdeStorage(): void {
    const stored = sessionStorage.getItem('listadoDescargas');
    if (stored) {
      this.listadoDescargas = JSON.parse(stored);
      // Convertir fechas de string a Date
      this.listadoDescargas.forEach((d) => {
        if (d.fechaCreacion) {
          d.fechaCreacion = new Date(d.fechaCreacion);
        }
      });
      this.notificarCambios();
      this.reiniciarPollingPendientes();
    }
  }

  private notificarCambios(): void {
    this.descargasSubject.next([...this.listadoDescargas]);
  }

  private reiniciarPollingPendientes(): void {
    // Reiniciar polling para todas las descargas que no estén listas
    this.listadoDescargas
      .filter((d) => !d.ready && d.token && d.idCamara)
      .forEach((d) => {
        this.iniciarPollingDescarga(d.idCamara!, d.token!);
      });
  }

  /**
   * Id único local para identificar un item del panel. No usa crypto.randomUUID
   * porque no existe en contextos no seguros (http://<ip>, no-localhost).
   */
  private nuevoToken(): string {
    return `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }

  public agregarDescargaExcel(
    nombre: string,
    fetcher: () => Promise<Blob>,
  ): void {
    const id = this.nuevoToken();
    const descarga: Descarga = {
      token: id,
      ready: false,
      tipo: 'excel',
      fechaCreacion: new Date(),
      nombre,
    };
    this.listadoDescargas.push(descarga);
    this.guardarEnStorage();
    this.notificarCambios();

    const inicio = Date.now();
    fetcher()
      .then((blob) => {
        const d = this.listadoDescargas.find((x) => x.token === id);
        if (d) {
          d.url = URL.createObjectURL(blob);
          d.ready = true;
          d.duracionMs = Date.now() - inicio;
          this.guardarEnStorage();
          this.notificarCambios();
          this.nuevaDescargaListaSubject.next(d);
        }
      })
      .catch(async (err) => {
        console.error('Error generando Excel:', err);
        // El detalle viene en el body, que con responseType 'blob' es un Blob:
        // hay que leerlo o se pierde la causa real (ej. el timeout de Mongo).
        const detalle = await this.detalleDeError(err);
        console.error('Detalle del servidor:', detalle ?? '(sin cuerpo)');
        // No se borra la fila: desaparecer sin explicación se lee como "no pasó
        // nada". Queda marcada con el error y el usuario la elimina cuando quiera.
        const d = this.listadoDescargas.find((x) => x.token === id);
        if (d) {
          d.error = this.mensajeDeError(err, detalle);
          d.duracionMs = Date.now() - inicio;
          this.guardarEnStorage();
          this.notificarCambios();
        }
      });
  }

  /**
   * Export ASÍNCRONO basado en job + polling. Crea el job (responde al instante),
   * muestra "Procesando..." y pollea el estado cada 10s; al completar, la descarga
   * apunta a la signed URL devuelta. No toca el timeout de Cloudflare.
   */
  public agregarDescargaJob(
    nombre: string,
    acciones: {
      crearJob: () => Promise<{ jobId: string }>;
      consultarEstado: (jobId: string) => Promise<DescargaJobEstado>;
      /**
       * Opcional: cuando el archivo no se sirve por una URL pública/firmada,
       * esto lo baja por el endpoint autenticado y el panel usa un blob URL
       * (mismo mecanismo que `agregarDescargaExcel`).
       */
      descargarArchivo?: (jobId: string) => Promise<Blob>;
    },
  ): void {
    // El create devuelve el jobId al instante (las paradas se resuelven en
    // background en el backend), así que usamos el jobId como id del item del
    // panel directamente.
    const inicio = Date.now();
    acciones
      .crearJob()
      .then(({ jobId }) => {
        const descarga: Descarga = {
          token: jobId,
          jobId,
          ready: false,
          tipo: 'excel',
          fechaCreacion: new Date(),
          nombre,
        };
        this.listadoDescargas.push(descarga);
        this.guardarEnStorage();
        this.notificarCambios();
        this.iniciarPollingJob(
          jobId,
          jobId,
          acciones.consultarEstado,
          inicio,
          acciones.descargarArchivo,
        );
      })
      .catch((err) => {
        // Si falla la creación, no se llegó a pushear ninguna fila.
        console.error('Error creando job de export:', err);
      });
  }

  /**
   * Cuerpo del error como texto. Con `responseType: 'blob'` el body llega como
   * Blob (o ArrayBuffer), así que un `err.error.message` directo da undefined y
   * la causa real se pierde.
   */
  private async detalleDeError(err: unknown): Promise<string | undefined> {
    const body = (err as { error?: unknown })?.error;
    try {
      let texto: string | undefined;
      if (body instanceof Blob) texto = await body.text();
      else if (body instanceof ArrayBuffer)
        texto = new TextDecoder().decode(body);
      else if (typeof body === 'string') texto = body;
      else if (body && typeof body === 'object') texto = JSON.stringify(body);
      if (!texto) return undefined;
      try {
        const json = JSON.parse(texto) as { message?: string | string[] };
        const msg = Array.isArray(json.message)
          ? json.message.join(', ')
          : json.message;
        return msg || texto;
      } catch {
        return texto;
      }
    } catch {
      return undefined;
    }
  }

  /** Texto corto y legible a partir de un HttpErrorResponse (o lo que venga). */
  private mensajeDeError(err: unknown, detalle?: string): string {
    const e = err as { status?: number; statusText?: string; message?: string };
    // Un timeout de la base es lo que más aparece en los exports grandes: se
    // nombra como tal en vez de dejar un 500 pelado.
    const esTimeoutDeBase =
      !!detalle && /deadline exceeded|i\/o timeout|timed out/i.test(detalle);
    const recorte = detalle
      ? ` — ${detalle.slice(0, 200)}${detalle.length > 200 ? '…' : ''}`
      : '';
    if (e?.status === 0) return 'Sin conexión con el servidor';
    if (e?.status === 504 || e?.status === 524)
      return 'El servidor tardó demasiado (timeout). Acotá el rango o el alcance.';
    if (esTimeoutDeBase)
      return 'La consulta tardó demasiado en la base y se cortó. Acotá el rango o el alcance y volvé a intentar.';
    if (e?.status === 500)
      return `Error del servidor generando el archivo (500). Probá acotar el rango o el alcance${recorte}`;
    if (e?.status) return `Error ${e.status} generando el archivo${recorte}`;
    return e?.message || 'Error generando el archivo';
  }

  private iniciarPollingJob(
    token: string,
    jobId: string,
    consultarEstado: (jobId: string) => Promise<DescargaJobEstado>,
    inicio: number,
    descargarArchivo?: (jobId: string) => Promise<Blob>,
  ): void {
    if (this.subscriptions.has(token)) return;

    const subscription = interval(10000)
      .pipe(
        switchMap(() =>
          from(consultarEstado(jobId)).pipe(
            catchError((error) => {
              console.warn(`Error temporal consultando job ${jobId}:`, error);
              return of(null as DescargaJobEstado | null);
            }),
          ),
        ),
      )
      .subscribe(async (estado: DescargaJobEstado | null) => {
        if (!estado) return;
        if (estado.estado === 'completado' && estado.urlDescarga) {
          const descarga = this.listadoDescargas.find((d) => d.token === token);
          if (descarga && !descarga.ready) {
            // Con `descargarArchivo` el archivo se baja autenticado y queda como
            // blob local; sin él, `urlDescarga` ya es descargable (signed URL).
            if (descargarArchivo) {
              try {
                const blob = await descargarArchivo(jobId);
                descarga.url = URL.createObjectURL(blob);
              } catch (err) {
                console.error(
                  `Error bajando el archivo del job ${jobId}:`,
                  err,
                );
                descarga.error = this.mensajeDeError(
                  err,
                  await this.detalleDeError(err),
                );
                this.guardarEnStorage();
                this.notificarCambios();
                this.detenerPollingDescarga(token);
                return;
              }
            } else {
              descarga.url = estado.urlDescarga;
            }
            descarga.ready = true;
            descarga.duracionMs = Date.now() - inicio;
            this.guardarEnStorage();
            this.notificarCambios();
            this.nuevaDescargaListaSubject.next(descarga);
          }
          this.detenerPollingDescarga(token);
        } else if (estado.estado === 'error') {
          console.error(`Export job ${jobId} falló:`, estado.error);
          this.detenerPollingDescarga(token);
          const descarga = this.listadoDescargas.find((d) => d.token === token);
          if (descarga) {
            descarga.error = estado.error || 'Error generando el archivo';
            descarga.duracionMs = Date.now() - inicio;
            this.guardarEnStorage();
            this.notificarCambios();
          }
        } else if (typeof estado.progreso === 'number') {
          // pendiente/procesando: reflejar el % en el panel de Descargas.
          const descarga = this.listadoDescargas.find((d) => d.token === token);
          if (descarga && descarga.progreso !== estado.progreso) {
            descarga.progreso = estado.progreso;
            this.notificarCambios();
          }
        }
      });

    this.subscriptions.set(token, subscription);
  }

  public limpiarDescargasCompletadas(): void {
    this.listadoDescargas = this.listadoDescargas.filter(
      (d) => !d.ready && !d.error,
    );
    this.guardarEnStorage();
    this.notificarCambios();
  }

  ngOnDestroy(): void {
    // Limpiar todas las subscripciones al destruir el servicio
    this.subscriptions.forEach((sub) => sub.unsubscribe());
    this.subscriptions.clear();
  }
}
